import { useEffect, useState } from "react";
import { Box, Clock3, Download, FileUp, HardDrive, Info, Plus, ShieldCheck, Sparkles, Trash2, WifiOff } from "lucide-react";
import { bundledTemplates } from "../model/templates";
import type { MotiveDocument } from "../model/types";
import { deleteProject, listRecentProjects, type ProjectRecord } from "../storage/database";

export function ProjectHome({
  onNew,
  onOpenFile,
  onOpenRecent,
  onOpenTemplate,
  installAvailable,
  onInstall,
  onAbout
}: {
  onNew: () => void;
  onOpenFile: () => void;
  onOpenRecent: (id: string) => void;
  onOpenTemplate: (document: MotiveDocument) => void;
  installAvailable: boolean;
  onInstall: () => void;
  onAbout: () => void;
}): React.JSX.Element {
  const [recent, setRecent] = useState<ProjectRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = (): void => {
    setLoading(true);
    void listRecentProjects().then(setRecent).finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  return (
    <main className="home-screen">
      <div className="home-topbar">
        <Brand />
        <div className="privacy-pill"><ShieldCheck size={15} /> Local only · no account</div>
      </div>
      <section className="home-hero">
        <div className="hero-copy">
          <span className="hero-kicker">Local-first parametric CAD</span>
          <h1>Design real things.</h1>
          <p>Create precise, editable solids for 3D printing and manufacturing. ModelMotive runs entirely on this device.</p>
          <div className="hero-actions">
            <button className="button primary large" onClick={onNew}><Plus size={18} /> New model</button>
            <button className="button secondary large" onClick={onOpenFile}><FileUp size={18} /> Open .motive</button>
            {installAvailable && <button className="button ghost large" onClick={onInstall}><Download size={18} /> Install app</button>}
          </div>
          <div className="local-benefits">
            <span><WifiOff size={15} /> Works offline</span>
            <span><HardDrive size={15} /> Saves locally</span>
            <span><ShieldCheck size={15} /> No telemetry</span>
          </div>
        </div>
        <div className="hero-visual" aria-hidden="true">
          <div className="hero-cube cube-a" />
          <div className="hero-cube cube-b" />
          <div className="hero-cube cube-c" />
          <div className="hero-grid" />
          <div className="hero-caption"><span>Parametric history</span><strong>Box → Hole → Fillet</strong></div>
        </div>
      </section>

      <section className="home-section">
        <div className="home-section-heading"><div><span className="eyebrow">Start with a recipe</span><h2>Motives</h2></div><Sparkles size={20} /></div>
        <div className="template-grid">
          {bundledTemplates.map((template, index) => (
            <button className="template-card" key={template.id} onClick={() => onOpenTemplate(template.create())}>
              <div className={`template-preview template-preview-${index + 1}`}><Box size={35} /></div>
              <span><strong>{template.title}</strong><small>{template.description}</small></span>
              <span className="template-open">Open</span>
            </button>
          ))}
          <button className="template-card blank" onClick={onNew}>
            <div className="template-preview"><Plus size={34} /></div>
            <span><strong>Blank model</strong><small>Start from an empty feature history</small></span>
            <span className="template-open">Create</span>
          </button>
        </div>
      </section>

      <section className="home-section recents-section">
        <div className="home-section-heading"><div><span className="eyebrow">On this device</span><h2>Recent projects</h2></div><Clock3 size={20} /></div>
        {loading ? <div className="recent-empty">Loading local projects…</div> : recent.length === 0 ? (
          <div className="recent-empty"><HardDrive size={24} /><p>No local projects yet. Your autosaved models will appear here.</p></div>
        ) : (
          <div className="recent-list">
            {recent.map((project) => (
              <div className="recent-row" key={project.id}>
                <button className="recent-open" onClick={() => onOpenRecent(project.id)}>
                  <span className="recent-icon"><Box size={18} /></span>
                  <span><strong>{project.name}</strong><small>{new Date(project.modifiedAt).toLocaleString()} · {project.storage.toUpperCase()}</small></span>
                </button>
                <button className="icon-button danger-ghost" title="Delete local project" onClick={() => void deleteProject(project.id).then(refresh)}><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        )}
      </section>

      <footer className="home-footer"><span>ModelMotive 0.1</span><span>Your models never leave your browser unless you export them.</span><button className="footer-link" onClick={onAbout}><Info size={12} /> About & licences</button></footer>
    </main>
  );
}

export function Brand(): React.JSX.Element {
  return (
    <div className="brand"><img src="./icons/icon.svg" alt="" /><span><strong>ModelMotive</strong><small>Modeling Engine</small></span></div>
  );
}
